<template>
  <v-menu
    offset-y
    close-on-click
    :max-width="maxWidth"
    :max-height="maxHeight"
    :disabled="disabled"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        v-bind="attrs"
        :color="color"
        :small="small"
        class="text-none"
        rounded
        elevation="0"
        max-width="300"
        v-on="on"
      >
        <v-icon
          v-if="useIcons"
          :left="!hideText"
          :color="selectedIconColor"
          :small="small"
          class="material-icons-outlined"
        >
          {{ selectedIcon }}
        </v-icon>
        <span
          v-if="!hideText"
          class="dropdown-selected-text"
          :class="selectedClass"
          :style="selectedStyle"
        >
          {{ selectedText }}
        </span>
        <v-icon v-if="!disabled" right :color="selectedIconColor">
          arrow_drop_down
        </v-icon>
      </v-btn>
    </template>
    <v-list>
      <template v-for="option in otherOptions">
        <v-subheader
          v-if="getOptionHeader(option)"
          :key="`${generateUniqueKey(option)}-header`"
        >
          {{ getOptionHeader(option) }}
        </v-subheader>
        <v-list-item
          v-else
          :key="generateUniqueKey(option)"
          @click="$emit('input', option)"
        >
          <v-list-item-title :class="optionClass">
            <v-icon
              left
              class="material-icons-outlined"
              :color="getOptionIconColor(option)"
            >
              {{ getOptionIcon(option) }}
            </v-icon>
            {{ getOptionText(option) }}
          </v-list-item-title>
        </v-list-item>
      </template>
    </v-list>
  </v-menu>
</template>

<script>
export default {
  name: 'Dropdown',
  props: {
    value: {
      type: [String, Number, Object],
      default: null,
    },
    keyProp: {
      type: String,
      default: 'id',
    },
    textProp: {
      type: String,
      default: null,
    },
    iconProp: {
      type: String,
      default: null,
    },
    iconColorProp: {
      type: String,
      default: null,
    },
    selectedIconColorProp: {
      type: String,
      default: null,
    },
    headerProp: {
      type: String,
      default: null,
    },
    color: {
      type: String,
      default: 'primary',
    },
    options: {
      type: Array,
      required: true,
    },
    placeholder: {
      type: String,
      default: 'Please select an option',
    },
    selectedClass: {
      type: String,
      default: 'caption font-weight-bold',
    },
    optionClass: {
      type: String,
      default: 'caption',
    },
    maxWidth: {
      type: [String, Number],
      default: '300',
    },
    maxHeight: {
      type: [String, Number],
      default: '400',
    },
    hideText: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    otherOptions() {
      return this.options.filter(
        (option) => option !== this.selected
      );
    },
    selectedText() {
      return this.selected
        ? this.getOptionText(this.selected)
        : this.placeholder;
    },
    selectedIcon() {
      return this.selected ? this.getOptionIcon(this.selected) : null;
    },
    selectedIconColor() {
      return this.selected
        ? this.getSelectedIconColor(this.selected)
        : null;
    },
    selected: {
      get: function () {
        return this.value;
      },
      set: function (value) {
        this.$emit('input', value);
      },
    },
    selectedStyle() {
      const isString = typeof this.maxWidth === 'string';
      if (isString) {
        const hasEndingPx = this.maxWidth.endsWith('px');
        const hasEndingPercent = this.maxWidth.endsWith('%');
        if (hasEndingPx || hasEndingPercent) {
          return {
            maxWidth: this.maxWidth,
          };
        }
      }
      const maxWidth = parseInt(this.maxWidth) - 50;
      return {
        maxWidth: maxWidth + 'px',
      };
    },
    useIcons() {
      return !!this.iconProp;
    },
  },
  mounted() {
    if (!this.value && !this.placeholder) {
      this.selected = this.options[0];
    }
  },
  methods: {
    getOptionText(option) {
      if (this.textProp) {
        return option[this.textProp];
      }
      return option;
    },
    getOptionIcon(option) {
      if (this.iconProp) {
        return option[this.iconProp];
      }
      return null;
    },
    getOptionIconColor(option) {
      if (this.iconColorProp) {
        return option[this.iconColorProp];
      }
      return null;
    },
    getSelectedIconColor(option) {
      if (this.selectedIconColorProp) {
        return option[this.selectedIconColorProp];
      }
      return null;
    },
    getOptionHeader(option) {
      if (this.headerProp) {
        return option[this.headerProp];
      }
      return null;
    },
    generateUniqueKey(option) {
      if (typeof option !== 'string') {
        return `${option[this.keyProp]}`;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dropdown-selected-text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
